// WHAT: Tab component for Withings synchronization status and manual sync options.
// WHY:  Provides interface for viewing sync status, performing smart sync, force sync, and date range sync operations for Withings health data.
// DOMAIN: Health → Withings
// DEPENDENCIES: React, @/components/ui, ../ui/WithingsSyncButton, ../../types, lucide-react
// RISKS:
//   - WARN: Sync operations are async; ensure proper loading state handling to prevent duplicate syncs.
//   - NOTE: Date range validation is minimal; invalid date ranges may cause sync failures.
//   - WARN: Force sync bypasses cache; may cause unnecessary API calls and rate limiting.

import { AlertCircle, Calendar, Download, RefreshCw } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/ui/Modal";

import { type SyncResult, type SyncStatus } from "../../types";
import { SyncResultCard } from "../molecules/SyncResultCard";
import { WithingsSyncButton } from "../ui";

interface SyncTabProperties {
  dateRangeEnd: string;
  dateRangeStart: string;
  isSyncing: boolean;
  onDateRangeSync: (start: string, end: string) => void;
  onForceSync: () => void;
  onSmartSync: () => void;
  setDateRangeEnd: (date: string) => void;
  setDateRangeStart: (date: string) => void;
  setShowDateRangeModal: (show: boolean) => void;
  showDateRangeModal: boolean;
  syncResult: null | SyncResult;
  syncStatus: SyncStatus;
}

/**
 * WHAT:
 *   Tab component for Withings synchronization status and manual sync options.
 *
 * WHY:
 *   Provides users with comprehensive interface for viewing sync status, performing
 *   smart sync (incremental), force sync (full refresh), and date range sync operations
 *   for Withings health data integration.
 *
 * PROPS:
 *   - syncStatus: SyncStatus - Current synchronization status with last sync date, total measurements, days since last sync
 *   - syncResult: SyncResult | null - Result of last sync operation
 *   - isSyncing: boolean - Flag indicating if sync is currently in progress
 *   - onSmartSync: () => void - Callback for smart sync operation
 *   - onForceSync: () => void - Callback for force sync operation
 *   - onDateRangeSync: () => void - Callback for date range sync operation
 *   - showDateRangeModal: boolean - Flag to show/hide date range selection modal
 *   - setShowDateRangeModal: (show: boolean) => void - Function to toggle date range modal
 *   - dateRangeStart: string - Start date for date range sync
 *   - setDateRangeStart: (date: string) => void - Function to set start date
 *   - dateRangeEnd: string - End date for date range sync
 *   - setDateRangeEnd: (date: string) => void - Function to set end date
 *
 * BEHAVIOR:
 *   - Displays sync status cards: last synchronization date, total measurements, days since last sync
 *   - Shows sync result with success/error indicators and detailed messages
 *   - Provides sync action buttons: smart sync, force sync, date range sync
 *   - Renders date range selection modal for custom date range syncs
 *   - Displays sync progress and loading states
 *   - Shows error messages if sync fails
 *
 * RISKS:
 *   - WARN: Sync operations are async; ensure proper loading state handling to prevent duplicate syncs.
 *   - NOTE: Date range validation is minimal; invalid date ranges may cause sync failures.
 *   - WARN: Force sync bypasses cache; may cause unnecessary API calls and rate limiting.
 *   - NOTE: Sync result display depends on parent component state; ensure state is properly managed.
 *
 * @param root0
 *
 * @param root0.syncStatus
 *
 * @param root0.syncResult
 *
 * @param root0.isSyncing
 *
 * @param root0.onSmartSync
 *
 * @param root0.onForceSync
 *
 * @param root0.onDateRangeSync
 *
 * @param root0.showDateRangeModal
 *
 * @param root0.setShowDateRangeModal
 *
 * @param root0.dateRangeStart
 *
 * @param root0.setDateRangeStart
 *
 * @param root0.dateRangeEnd
 *
 * @param root0.setDateRangeEnd
 *
 * @returns {JSX.Element} Sync tab component with synchronization status and actions
 *
 */
export const SyncTab: React.FC<SyncTabProperties> = ({
  dateRangeEnd,
  dateRangeStart,
  isSyncing,
  onDateRangeSync,
  onForceSync,
  onSmartSync,
  setDateRangeEnd,
  setDateRangeStart,
  setShowDateRangeModal,
  showDateRangeModal,
  syncResult,
  syncStatus,
}) => {
  return (
    <div className="space-y-6">
      {/* Sync Status */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-white">Synchronization Status</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-slate-800/50 border-slate-700 text-white">
            <div className="p-4 text-center">
              <p className="text-slate-400 text-sm mb-2">Last Synchronization</p>
              <p className="text-lg font-semibold text-white">
                {syncStatus.lastSyncDate
                  ? new Date(syncStatus.lastSyncDate).toLocaleDateString("en-US")
                  : "Never"}
              </p>
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 text-white">
            <div className="p-4 text-center">
              <p className="text-slate-400 text-sm mb-2">Total Measurements</p>
              <p className="text-lg font-semibold text-white">{syncStatus.totalMeasurements}</p>
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 text-white">
            <div className="p-4 text-center">
              <p className="text-slate-400 text-sm mb-2">Days Since Last Sync</p>
              <p className="text-lg font-semibold text-white">
                {syncStatus.daysSinceLastSync ?? "N/A"}
              </p>
            </div>
          </Card>
        </div>
      </div>

      {/* Sync Actions */}
      <Card className="bg-slate-800/50 border-slate-700 text-white">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Synchronization Actions</h3>

          <div className="flex flex-wrap gap-4">
            <WithingsSyncButton onSyncComplete={onSmartSync} variant="smart" />

            <WithingsSyncButton onSyncComplete={onForceSync} variant="force" />

            <Button
              disabled={isSyncing}
              icon={<Calendar className="h-4 w-4" />}
              onClick={() => {
                setShowDateRangeModal(true);
              }}
              variant="outline"
            >
              Sync Date Range
            </Button>
          </div>
        </div>
      </Card>

      {/* Sync Result */}
      {syncResult ? <SyncResultCard syncResult={syncResult} /> : null}

      {/* Date Range Sync Modal */}
      <Modal
        isOpen={showDateRangeModal}
        onClose={() => {
          setShowDateRangeModal(false);
        }}
        size="md"
      >
        <ModalHeader>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-400" />
            <h3 className="text-xl font-semibold text-white">Sync Date Range</h3>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">Start Date</label>
              <Input
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => {
                  setDateRangeStart(e.target.value);
                }}
                placeholder="Select start date"
                type="date"
                value={dateRangeStart}
              />
            </div>

            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">End Date</label>
              <Input
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => {
                  setDateRangeEnd(e.target.value);
                }}
                placeholder="Select end date"
                type="date"
                value={dateRangeEnd}
              />
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-300">
                  <p className="font-medium mb-1">Note:</p>
                  <p>Select a date range to synchronize historical data.</p>
                  <p className="mt-1">
                    Example: 01/01/2025 - 08/07/2025 for all data up to the last sync.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="flex gap-3 w-full">
            <Button
              className="flex-1"
              disabled={isSyncing || !dateRangeStart || !dateRangeEnd}
              icon={
                isSyncing ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )
              }
              onClick={() => {
                onDateRangeSync(dateRangeStart, dateRangeEnd);
              }}
            >
              {isSyncing ? "Syncing..." : "Sync Date Range"}
            </Button>

            <Button
              onClick={() => {
                setShowDateRangeModal(false);
              }}
              variant="outline"
            >
              Cancel
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};
